import { Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import {
  DEPLOY_RELOAD_STORAGE_KEY,
  cacheBustedHref,
  deployRefreshEnabled,
  entryScriptNameFromHtml,
  entryScriptNameFromSrc,
  isStaleBuildError,
  shouldReloadForDeploy,
} from './deploy-refresh.utils';

/** How often an open tab asks whether a new build is deployed. */
const DEPLOY_CHECK_INTERVAL_MS = 60_000;

/**
 * Reloads an open tab once when a new build is deployed.
 * Hashed lazy chunks from the previous build 404, and the page stays on the old screen.
 */
@Injectable({ providedIn: 'root' })
export class DeployRefreshService {
  /** True after {@link start} has attached listeners. */
  private started = false;

  /**
   * Watches for a deployed build and for a lazy route whose file is gone.
   * No-op on the local dev server.
   * @param router - App router, used for failed lazy navigations
   */
  start(router: Router): void {
    if (this.started || !deployRefreshEnabled(environment.env)) {
      return;
    }
    this.started = true;
    this.stripCacheBuster();
    router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        this.reloadIfStaleBuild(event.error);
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.checkDeployedBuild();
      }
    });
    window.addEventListener('pageshow', () => {
      void this.checkDeployedBuild();
    });
    window.setInterval(() => {
      void this.checkDeployedBuild();
    }, DEPLOY_CHECK_INTERVAL_MS);
    void this.checkDeployedBuild();
  }

  /**
   * Reloads when the error is a missing build file.
   * @param error - Caught navigation or global error
   * @returns True when a reload was started, so the caller can skip a toast
   */
  reloadIfStaleBuild(error: unknown): boolean {
    if (!deployRefreshEnabled(environment.env) || !isStaleBuildError(error)) {
      return false;
    }
    return this.reloadFor('chunk');
  }

  /**
   * Compares the running entry script with the entry script in the deployed document.
   */
  private async checkDeployedBuild(): Promise<void> {
    const current = this.loadedEntryScript();
    const deployed = await this.fetchDeployedEntryScript();
    if (!current || !deployed) {
      return;
    }
    if (current === deployed) {
      sessionStorage.removeItem(DEPLOY_RELOAD_STORAGE_KEY);
      return;
    }
    this.reloadFor(deployed);
  }

  /**
   * File name of the `main` script this document loaded.
   * @returns Entry script name, or null when the page has no `main` script
   */
  private loadedEntryScript(): string | null {
    const scripts = document.querySelectorAll('script[src]');
    for (const script of scripts) {
      const name = entryScriptNameFromSrc(script.getAttribute('src') ?? '');
      if (name) {
        return name;
      }
    }
    return null;
  }

  /**
   * File name of the `main` script in the document currently deployed.
   * @returns Entry script name, or null when the document cannot be read
   */
  private async fetchDeployedEntryScript(): Promise<string | null> {
    try {
      const response = await fetch(new URL('.', document.baseURI), {
        cache: 'no-store',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) {
        return null;
      }
      return entryScriptNameFromHtml(await response.text());
    } catch {
      return null;
    }
  }

  /**
   * Navigates to the same address with a cache-busting query, at most once per target.
   * @param target - Deployed entry file, or `chunk` after a failed lazy load
   * @returns True when the navigation started
   */
  private reloadFor(target: string): boolean {
    const stored = sessionStorage.getItem(DEPLOY_RELOAD_STORAGE_KEY);
    if (!shouldReloadForDeploy(stored, target)) {
      return false;
    }
    sessionStorage.setItem(DEPLOY_RELOAD_STORAGE_KEY, target);
    window.location.replace(cacheBustedHref(window.location.href, Date.now()));
    return true;
  }

  /**
   * Removes the cache-busting query left by the previous reload.
   */
  private stripCacheBuster(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_')) {
      return;
    }
    url.searchParams.delete('_');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }
}
